'use client';

/**
 * Presentational mode views for `ExperienceStep`. Extracted from
 * `experience-step.tsx` (conformance, code-size guard). These render the three
 * mode-specific layouts (select-affiliation radio list, select-experience
 * checkbox list, profile-mode category sections). All state + handlers live in
 * the parent `ExperienceStep`; these components receive lists, the rendered
 * add-form node, and callbacks as props — behaviour is identical to the
 * previous inline returns.
 */
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Award, Building2, GraduationCap, Plus } from 'lucide-react';

import { Button } from '@kit/ui/button';
import { Card, CardContent } from '@kit/ui/card';
import { Checkbox } from '@kit/ui/checkbox';
import { If } from '@kit/ui/if';
import { Label } from '@kit/ui/label';
import { RadioGroup, RadioGroupItem } from '@kit/ui/radio-group';
import { Trans } from '@kit/ui/trans';

import {
  AwardCard,
  ExperienceCard,
  formatMonthYear,
  getOrgDisplayName,
  getTypeIcon,
  type Experience,
} from './experience-step-helpers';

function LoadingState({ isLoading }: { isLoading: boolean }) {
  return (
    <If condition={isLoading}>
      <div className="text-muted-foreground py-8 text-center" role="status">
        <Trans i18nKey="dancer:profileSetup.experience.loading" />
      </div>
    </If>
  );
}

// ---------------------------------------------------------------------------
// SELECT-AFFILIATION mode — RadioGroup of current school/company
// ---------------------------------------------------------------------------

interface SelectAffiliationViewProps {
  candidates: Experience[];
  selectedSingleId: string | null;
  onSelectSingle: (id: string) => void;
  showAddForm: boolean;
  addForm: ReactNode;
  onAddNew: () => void;
  isLoading: boolean;
}

export function SelectAffiliationView({
  candidates,
  selectedSingleId,
  onSelectSingle,
  showAddForm,
  addForm,
  onAddNew,
  isLoading,
}: SelectAffiliationViewProps) {
  return (
    <div className="space-y-6 pb-28 sm:pb-8">
      {candidates.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No current affiliations on file. Add one below.
        </p>
      ) : (
        <RadioGroup
          value={selectedSingleId ?? ''}
          onValueChange={onSelectSingle}
          className="space-y-2"
        >
          {candidates.map((exp) => {
            const Icon = exp.type === 'school' ? GraduationCap : Building2;
            return (
              <Card key={exp.id}>
                <CardContent className="flex items-center gap-3 p-4">
                  <RadioGroupItem value={exp.id} id={`aff-${exp.id}`} />
                  <Icon className="text-muted-foreground h-5 w-5" />
                  <Label
                    htmlFor={`aff-${exp.id}`}
                    className="flex-1 cursor-pointer"
                  >
                    <div className="font-medium">{getOrgDisplayName(exp)}</div>
                    {exp.title && (
                      <div className="text-muted-foreground text-xs">
                        {exp.title}
                      </div>
                    )}
                  </Label>
                </CardContent>
              </Card>
            );
          })}
        </RadioGroup>
      )}

      {showAddForm ? (
        addForm
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={onAddNew}
          className="w-full"
        >
          <Plus className="mr-2 h-4 w-4" />
          <Trans i18nKey="dancer:profileSetup.experience.addNewSchoolOrCompany" />
        </Button>
      )}

      <LoadingState isLoading={isLoading} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// SELECT-EXPERIENCE mode — checkbox list of past experiences
// ---------------------------------------------------------------------------

interface SelectExperienceViewProps {
  candidates: Experience[];
  selectedMultiIds: string[];
  onTogglePast: (id: string, checked: boolean) => void;
  showAddForm: boolean;
  addForm: ReactNode;
  onAddNew: () => void;
  isLoading: boolean;
}

export function SelectExperienceView({
  candidates,
  selectedMultiIds,
  onTogglePast,
  showAddForm,
  addForm,
  onAddNew,
  isLoading,
}: SelectExperienceViewProps) {
  return (
    <div className="space-y-6 pb-28 sm:pb-8">
      {candidates.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          No past experience on file yet. Add one below.
        </p>
      ) : (
        <div className="space-y-2">
          {candidates.map((exp) => (
            <Card key={exp.id}>
              <CardContent className="flex items-center gap-3 p-4">
                <Checkbox
                  id={`past-${exp.id}`}
                  checked={selectedMultiIds.includes(exp.id)}
                  onCheckedChange={(c) => onTogglePast(exp.id, c === true)}
                />
                <div className="mt-0">{getTypeIcon(exp.type)}</div>
                <Label
                  htmlFor={`past-${exp.id}`}
                  className="flex-1 cursor-pointer"
                >
                  <div className="font-medium">{getOrgDisplayName(exp)}</div>
                  <div className="text-muted-foreground text-xs">
                    {exp.type}
                    {exp.start_date && ` · ${formatMonthYear(exp.start_date)}`}
                    {exp.end_date && ` – ${formatMonthYear(exp.end_date)}`}
                  </div>
                </Label>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showAddForm ? (
        addForm
      ) : (
        <Button
          type="button"
          variant="outline"
          onClick={onAddNew}
          className="w-full"
        >
          <Plus className="mr-2 h-4 w-4" />
          <Trans i18nKey="dancer:profileSetup.experience.addPastExperience" />
        </Button>
      )}

      <LoadingState isLoading={isLoading} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PROFILE mode (default) — full management with category groups
// ---------------------------------------------------------------------------

interface ExperienceProfileViewProps {
  experienceList: Experience[];
  awardList: Experience[];
  showExperienceSection: boolean;
  showAwardSection: boolean;
  onlyAwards: boolean;
  hasAnyExperience: boolean;
  title?: string;
  showAddForm: boolean;
  addForm: ReactNode;
  isPending: boolean;
  editingId: string | null;
  actorRole: 'self' | 'admin';
  /** Suppresses all editing affordances (add form, add buttons, per-card edit/delete). */
  readOnly?: boolean;
  t: TFunction<'dancer'>;
  onEdit: (exp: Experience) => void;
  onDelete: (id: string) => void;
  onOpenExperienceForm: () => void;
  onOpenAwardForm: () => void;
  freelancerSection?: ReactNode;
  isLoading: boolean;
}

export function ExperienceProfileView({
  experienceList,
  awardList,
  showExperienceSection,
  showAwardSection,
  onlyAwards,
  hasAnyExperience,
  title,
  showAddForm,
  addForm,
  isPending,
  editingId,
  actorRole,
  readOnly = false,
  t,
  onEdit,
  onDelete,
  onOpenExperienceForm,
  onOpenAwardForm,
  freelancerSection,
  isLoading,
}: ExperienceProfileViewProps) {
  return (
    <div className="space-y-6 pb-28 sm:pb-8">
      {/* Add Experience Form (always rendered; visibility toggled).
          Hidden entirely in read-only mode so browsing can never add. */}
      <If condition={showAddForm && !readOnly}>{addForm}</If>

      {/* Experiences section
       *
       * `type='school'` rows render here AND in the lightweight
       * "Dance education" subform inside the setup wizard. Both
       * surfaces are valid entry points (rich card for org-linked +
       * ranked schools, lightweight subform for free-text schools).
       * Hiding schools here would cause newly-added rich-card schools
       * to disappear until the next page reload, which is worse UX
       * than a duplicate render. A future iteration can fold them
       * into a single surface; for now both share the same
       * `experiences` table.
       */}
      <If condition={showExperienceSection && experienceList.length > 0}>
        <div className="space-y-3">
          <h3 className="font-semibold">
            {title ? (
              title
            ) : (
              <Trans i18nKey="dancer:profileSetup.experience.yourExperiences" />
            )}
          </h3>
          {experienceList.map((exp) => (
            <ExperienceCard
              key={exp.id}
              exp={exp}
              isPending={isPending}
              editingId={editingId}
              actorRole={actorRole}
              readOnly={readOnly}
              t={t}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      </If>

      {/* Awards & Achievements section */}
      <If condition={showAwardSection && awardList.length > 0}>
        <div className="space-y-3">
          <h3 className="font-semibold">
            {onlyAwards && title ? (
              title
            ) : (
              <Trans
                i18nKey="dancer:profileSetup.experience.awardsAndAchievements"
                defaults="Awards & achievements"
              />
            )}
          </h3>
          {awardList.map((award) => (
            <AwardCard
              key={award.id}
              award={award}
              isPending={isPending}
              editingId={editingId}
              actorRole={actorRole}
              readOnly={readOnly}
              t={t}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      </If>

      {/* Add buttons — placed below the lists so the saved entry remains
          the visual anchor and "Add another …" is the unmistakable next
          step. The form's submit button reads "Save …" so it's never
          confused with these "Add …" entry-point buttons.
          Hidden in read-only mode. */}
      <If condition={!showAddForm && !readOnly}>
        <div className="flex flex-col gap-2 sm:flex-row">
          <If condition={showExperienceSection}>
            <Button
              type="button"
              variant={hasAnyExperience ? 'default' : 'outline'}
              onClick={onOpenExperienceForm}
              className="flex-1"
            >
              <Plus className="mr-2 h-4 w-4" />
              {hasAnyExperience ? (
                <Trans
                  i18nKey="dancer:profileSetup.experience.addAnotherExperience"
                  defaults="Add another experience"
                />
              ) : (
                <Trans
                  i18nKey="dancer:profileSetup.experience.addExperience"
                  defaults="Add experience"
                />
              )}
            </Button>
          </If>
          <If condition={showAwardSection}>
            <Button
              type="button"
              variant="outline"
              onClick={onOpenAwardForm}
              className="flex-1"
            >
              <Award className="mr-2 h-4 w-4" />
              <Trans
                i18nKey="dancer:profileSetup.experience.addAwardOrAchievement"
                defaults="Add award/achievement"
              />
            </Button>
          </If>
        </div>
      </If>

      {/* Setup-wizard-only chrome (freelancer toggle, brand-gated cards).
       * Apply wizard passes nothing here. */}
      {freelancerSection}

      {/* Loading State */}
      <LoadingState isLoading={isLoading} />
    </div>
  );
}
